import { Component } from '@theme/component';

/**
 * DAVIDsTEA generic expander: a clamped block of copy and the control that opens it.
 *
 * Written for the sustainability cards, whose "Learn More" has no destination: it expands the
 * copy in place instead of navigating. The component owns none of that geometry: it flips
 * `is-expanded` on itself and each section decides in CSS what that releases.
 *
 * The element is laid out with `display: contents` by its host section, so wrapping copy and
 * control together costs nothing in the flex column they sit in. Sections reach the card
 * around it with `:has(.is-expanded)`.
 *
 * The control hides itself when the copy already fits, which is the only honest way to print
 * a "read more" affordance: comparing scrollHeight to clientHeight while collapsed. Re-run on
 * resize because both cards are fluid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} clamped
 * @property {HTMLButtonElement} toggle
 *
 * @extends {Component<Refs>}
 */
export class DtExpandable extends Component {
  requiredRefs = ['clamped', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#syncToggleVisibility();

    if ('ResizeObserver' in window) {
      this.#observer = new ResizeObserver(() => this.#syncToggleVisibility());
      this.#observer.observe(this.refs.clamped);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }

  toggle() {
    const expanded = this.classList.toggle('is-expanded');
    const { toggle } = this.refs;

    toggle.setAttribute('aria-expanded', String(expanded));

    // Only the label text is swapped. The arrow, or whatever else the section puts in the
    // button, is a sibling element and is left alone.
    const label = toggle.querySelector('[data-expandable-label]');
    if (label) {
      const next = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
      if (next) label.textContent = next;
    }
  }

  #syncToggleVisibility() {
    if (this.classList.contains('is-expanded')) return;

    const { clamped, toggle } = this.refs;

    toggle.hidden = clamped.scrollHeight - clamped.clientHeight <= 1;
  }
}

if (!customElements.get('dt-expandable')) {
  customElements.define('dt-expandable', DtExpandable);
}
